import { useEffect, useState } from "react";

import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";

import { Alert, Button } from "@mui/material";
import { PageContainer } from "@/features/ui";

import { PurchasePaymentForm } from "@/features/purchase-payments";
import { apiOutletClient, getErrorMessage } from "@/features/shared";
import { CenteredLoading, useSnackbar } from "@/features/ui";

export const EditPurchasePayment = () => {
  const { referenceNumber } = useParams();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);
  const [defaultValues, setDefaultValues] = useState(null);
  const [convertedSalesPayment, setConvertedSalesPayment] = useState(null);

  useEffect(() => {
    const fetchPurchasePayment = async () => {
      setIsLoading(true);
      try {
        const {
          data: { data },
        } = await apiOutletClient.get(`purchase-payments/${referenceNumber}`, {
          params: {
            withBuyer: true,
            withSeller: true,
            withOutletChartOfAccount: true,
            withCashBankOutletChartOfAccount: true,
            withPurchaseInvoicePayments: true,
            withCreator: true,
          },
        });

        setConvertedSalesPayment(data.convertedSalesPayment ?? null);
        setDefaultValues({
          transactionDate: dayjs(data.transactionDate),
          buyer: data.buyer,
          seller: data.seller,
          // The form field is named outletChartOfAccount and maps to
          // cashBankOutletChartOfAccountId on submit; the API sends it as
          // cashBankOutletChartOfAccount.
          outletChartOfAccount: data.cashBankOutletChartOfAccount,
          note: data.note,
          purchaseInvoicePayments: data.purchaseInvoicePayments.map(
            (payment) => ({
              id: payment.id,
              purchaseInvoiceId: payment.purchaseInvoiceId,
              referenceNumber: payment.purchaseInvoice.referenceNumber,
              sellerReferenceNumber:
                payment.purchaseInvoice.sellerReferenceNumber,
              transactionDate: payment.purchaseInvoice.transactionDate,
              grandTotal: payment.purchaseInvoice.grandTotal,
              outstandingAmount: payment.outstandingAmount,
              allocatedAmount: payment.allocatedAmount,
              discount: payment.discount,
              discountAccounts: (payment.discountAccounts ?? []).map(
                (discountAccount) => ({
                  id: discountAccount.id,
                  outletChartOfAccount: discountAccount.outletChartOfAccount,
                  amount: discountAccount.amount,
                  // The note isn't persisted on the discount row (it only lives on
                  // the journal voucher line), so it can't be rehydrated on edit.
                  note: "",
                })
              ),
            })
          ),
        });
      } catch (error) {
        showSnackbar(
          getErrorMessage(error, "Gagal memperbarui pembayaran pembelian")
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchPurchasePayment();
  }, [referenceNumber, showSnackbar]);

  const handleSubmit = async (formData) => {
    try {
      // The form already maps discountAccounts into the backend shape
      // ({ outletChartOfAccountId, amount, note }); the buyer is the acting
      // outlet, so it travels in the header instead of the body.
      const { buyerId, ...payload } = formData;

      await apiOutletClient.put(
        `purchase-payments/${referenceNumber}`,
        payload,
        {
          headers: {
            "Outlet-Id": buyerId,
          },
        }
      );
      navigate(-1);
    } catch (error) {
      showSnackbar(
        getErrorMessage(error, "Gagal memperbarui pembayaran pembelian")
      );
    }
  };

  if (!defaultValues && isLoading) {
    return <CenteredLoading />;
  }

  return (
    <PageContainer
      maxWidth="xl"
      title="Ubah Pembayaran Pembelian"
      breadcrumbs={[
        {
          title: "Pembayaran Pembelian",
          path: "/procurement/purchase-payments",
        },
        {
          title: referenceNumber,
          path: `/procurement/purchase-payments/${referenceNumber}`,
        },
        { title: "Ubah" },
      ]}
    >
      {convertedSalesPayment && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Pembayaran ini sudah dikonversi menjadi penerimaan penjualan{" "}
          <strong>{convertedSalesPayment.referenceNumber}</strong>. Alokasi
          faktur dan diskonnya terkunci — hanya tanggal, akun kas &amp; bank,
          dan catatan yang dapat diubah.
        </Alert>
      )}
      <PurchasePaymentForm
        editMode={true}
        headerOnly={Boolean(convertedSalesPayment)}
        isLoading={isLoading}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        renderActionButton={({ isSubmitting }) => (
          <Button type="submit" variant="contained" loading={isSubmitting}>
            Simpan
          </Button>
        )}
      />
    </PageContainer>
  );
};
